import { Box, Button, FormControl, HStack, Input, Modal, ModalBody, ModalCloseButton, ModalContent, ModalHeader, ModalOverlay, Radio, RadioGroup, SimpleGrid, Spinner, Stack, Text, useDisclosure, useToast, VStack } from "@chakra-ui/react";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Grid from '@toast-ui/react-grid';
import { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import 'tui-grid/dist/tui-grid.min.css';
import type { FormatterProps } from 'tui-grid/types/store/column';
import { adminCreateCompanies, adminDeleteMedicines, adminGenerateFields, adminGetMedicines, adminTempAction, adminUpdateMedicine } from "../../../api";
import AdminOnly from "../../../components/Auth/AdminOnly";
import MyIcon from "../../../components/Icon/MyIcon";
import { colors } from "../../../styles";
import { IAdminMedicine, IAdminMedicineList } from "../../../types";
import CreateMedModal from "./CreateMedModal";
import PackImageEditModal from "./PackImageEditModal";

class PackImageEditButtonRenderer {
    static onOpenHandler?: (medicineId: string) => void;

    private el: HTMLDivElement;
    private buttonEl: HTMLButtonElement;
    private grid: any;
    private rowKey: any;
    private onOpen?: (medicineId: string) => void;
    private medicineIdValue?: string;

    constructor(props: any, options?: any) {

        //////////////////////////////////////////////////////////////

        this.el = document.createElement('div');
        this.el.style.width = '100%';
        this.el.style.height = '100%';
        this.el.style.display = 'flex';
        this.el.style.alignItems = 'center';
        this.el.style.justifyContent = 'center';
        this.el.style.boxSizing = 'border-box';
        this.el.style.pointerEvents = 'auto';

        //////////////////////////////////////////////////////////////

        this.buttonEl = document.createElement('button');
        this.buttonEl.type = 'button';
        this.buttonEl.textContent = '✐';

        ////////////////////////////////////////////////////////////// Grid 내부 DOM이므로 Chakra/React 스타일 의존을 줄임

        this.buttonEl.style.padding = '0';
        this.buttonEl.style.color = '#2B6CB0';
        this.buttonEl.style.cursor = 'pointer';
        this.buttonEl.style.fontSize = '12px';
        this.buttonEl.style.fontWeight = '600';
        this.buttonEl.style.pointerEvents = 'auto';
        this.buttonEl.style.width = '100%';
        this.buttonEl.style.height = '100%';
        this.buttonEl.style.display = 'flex';
        this.buttonEl.style.alignItems = 'center';
        this.buttonEl.style.justifyContent = 'center';

        this.el.appendChild(this.buttonEl);

        // TUI Grid/React wrapper 버전에 따라 renderer options 전달 경로가 다를 수 있어
        // 우선 static handler를 최우선으로 사용합니다.

        this.onOpen = PackImageEditButtonRenderer.onOpenHandler ?? options?.onOpen ?? props?.onOpen;
        this.rowKey = props?.rowKey;

        // CellRendererProps.value는 constructor 시점에도 들어올 수 있음

        this.medicineIdValue = props?.value !== undefined ? String(props.value) : undefined;

        this.buttonEl.addEventListener('click', (ev) => {
            ev.stopPropagation();
            // rowKey가 index/uuid 등 다양한 형태로 들어올 수 있어
            // grid.getData() 기준으로 실제 row의 id를 우선 추출합니다.
            const data = this.grid?.getData ? this.grid.getData() : [];
            const rowByIndex = (typeof this.rowKey === 'number') ? data?.[this.rowKey] : undefined;
            const rowByKey = Array.isArray(data)
                ? data.find((row: any) => row?.rowKey === this.rowKey || row?.id === this.rowKey)
                : undefined;

            const medicineId =
                this.medicineIdValue ??
                rowByIndex?.id ??
                rowByKey?.id ??
                (this.grid?.getValue ? this.grid.getValue(this.rowKey, 'id') : undefined) ??
                this.rowKey;

            if (medicineId !== undefined && medicineId !== null && this.onOpen) {
                this.onOpen(String(medicineId));
            }
        });
    }

    getElement() {
        return this.el;
    }

    render(props: any) {
        this.grid = props.grid;

        // react wrapper에서는 props.rowKey / options 전달 경로가 다를 수 있어
        // constructor에서 잡아둔 값을 우선으로 유지합니다.

        this.rowKey = props?.rowKey ?? this.rowKey;

        // packImageEdit 컬럼의 value를 medicine id로 심어두고 사용합니다.
        this.medicineIdValue = props?.value !== undefined ? String(props.value) : undefined;
        // 최신 핸들러로 갱신 (컴포넌트 re-render 시 closure가 바뀔 수 있음)
        this.onOpen = PackImageEditButtonRenderer.onOpenHandler ?? this.onOpen;
    }
}

export default function Medicines() {

    /* pagination */

    const [page, setPage] = useState(1);
    const size = 100;

    /* query */

    /* state - search filters */

    const [representativeCode, setRepresentativeCode] = useState<string>("");
    const [standardCode, setStandardCode] = useState<string>("");
    const [seqNo, setSeqNo] = useState<string>("");
    const [nameKor, setNameKor] = useState<string>("");
    const [nameEng, setNameEng] = useState<string>("");
    const [category, setCategory] = useState<string>("");
    const [maker, setMaker] = useState<string>("");
    const [seller, setSeller] = useState<string>("");
    const [dosageRoute, setDosageRoute] = useState<string>("");
    const [packageUnit, setPackageUnit] = useState<string>("");
    const [packageQuantity, setPackageQuantity] = useState<string>("");
    const [packageType, setPackageType] = useState<string>("");
    const [packageForm, setPackageForm] = useState<string>("");
    const [basis, setBasis] = useState<string>("");
    const [ingredientInfo, setIngredientInfo] = useState<string>("");
    const [ingredientEng, setIngredientEng] = useState<string>("");
    const [packImage, setPackImage] = useState<string>("");
    const [pillImage, setPillImage] = useState<string>("");
    const [ingredientCode, setIngredientCode] = useState<string>("");
    const [atcCode, setAtcCode] = useState<string>("");
    const [classification, setClassification] = useState<string>("");
    const [basePrice, setBasePrice] = useState<string>("");
    const [efficacy, setEfficacy] = useState<string>("");
    const [note, setNote] = useState<string>("");
    const [keyword, setKeyword] = useState<string>("");
    const [isDisplay, setIsDisplay] = useState<string>("true");

    const [searchKeyword, setSearchKeyword] = useState<string>("");

    /* query */

    const { data: medicineListData, isLoading: isMedicineListDataLoading } = useQuery<IAdminMedicineList>({
        queryKey: ['adminGetMedicines', page, size, isDisplay,
                                                    representativeCode,
                                                    standardCode,
                                                    seqNo,
                                                    nameKor,
                                                    nameEng,
                                                    category,
                                                    maker,
                                                    seller,
                                                    dosageRoute,
                                                    packageUnit,
                                                    packageQuantity,
                                                    packageType,
                                                    packageForm,
                                                    basis,
                                                    ingredientInfo,
                                                    ingredientEng,
                                                    packImage,
                                                    pillImage,
                                                    ingredientCode,
                                                    atcCode,
                                                    classification,
                                                    basePrice,
                                                    efficacy,
                                                    note,
                                                    keyword,
                                                searchKeyword],
        queryFn: () => adminGetMedicines({ 
                                            page, 
                                            size,
                                                isDisplay,
                                                representativeCode,
                                                standardCode,
                                                seqNo,
                                                nameKor,
                                                nameEng,
                                                category,
                                                maker,
                                                seller, 
                                                dosageRoute,
                                                packageUnit, 
                                                packageQuantity,
                                                packageType,
                                                packageForm,
                                                basis,
                                                ingredientInfo, 
                                                ingredientEng,
                                                packImage, 
                                                pillImage, 
                                                ingredientCode, 
                                                atcCode, 
                                                classification, 
                                                basePrice, 
                                                efficacy, 
                                                note,
                                                keyword,
                                            searchKeyword 
                                        }),
        enabled: true,
        // 빠른 체감 개선: 포커스 전환 등으로 불필요한 재요청 방지
        staleTime: 60 * 1000,
        placeholderData: keepPreviousData,
    });

    const totalPages = useMemo(() => medicineListData?.totalPages ?? 0, [medicineListData]);
    const currentPage = useMemo(() => medicineListData?.currentPage ?? 0, [medicineListData]);

    /* pagination */

    const pages = useMemo(() => {
        const startPage = Math.max(page - 4, 1);
        const endPage = Math.min(page + 5, totalPages);
        return Array.from({ length: Math.max(0, endPage - startPage + 1) }, (_, idx) => startPage + idx);
    }, [page, totalPages]);

    /* form */

    interface ISearchForm {
        representativeCode: string;
        standardCode: string;
        seqNo: string;
        nameKor: string;
        nameEng: string;
        category: string;
        maker: string;
        seller: string;
        dosageRoute: string;
        packageUnit: string;
        packageQuantity: string;
        packageType: string;
        packageForm: string;
        basis: string;
        ingredientInfo: string;
        ingredientEng: string;
        packImage: string;
        pillImage: string;
        ingredientCode: string;
        atcCode: string;
        classification: string;
        basePrice: string;
        efficacy: string;
        note: string;
        keyword: string;
        searchKeyword: string;
    }

    const { register, handleSubmit, formState : {errors}, reset } = useForm<ISearchForm>({
        mode: "onChange",
    });

    const onSubmit = (data: ISearchForm) => {

        setPage(1);

        setRepresentativeCode(data.representativeCode);
        setStandardCode(data.standardCode);
        setSeqNo(data.seqNo);
        setNameKor(data.nameKor);
        setNameEng(data.nameEng);
        setCategory(data.category);
        setMaker(data.maker);
        setSeller(data.seller);
        setDosageRoute(data.dosageRoute);
        setPackageUnit(data.packageUnit);
        setPackageQuantity(data.packageQuantity);
        setPackageType(data.packageType);
        setPackageForm(data.packageForm);
        setBasis(data.basis);
        setIngredientInfo(data.ingredientInfo);
        setIngredientEng(data.ingredientEng);
        setPackImage(data.packImage);
        setPillImage(data.pillImage);
        setIngredientCode(data.ingredientCode);
        setAtcCode(data.atcCode);
        setClassification(data.classification);
        setBasePrice(data.basePrice);
        setEfficacy(data.efficacy);
        setNote(data.note);
        setKeyword(data.keyword);
        setSearchKeyword(data.searchKeyword);
    }

    const onReset = () => {
        
        reset();
        setPage(1);

        setRepresentativeCode("");
        setStandardCode("");
        setSeqNo("");
        setNameKor("");
        setNameEng("");
        setCategory("");
        setMaker("");
        setSeller("");
        setDosageRoute("");
        setPackageUnit("");
        setPackageQuantity("");
        setPackageType("");
        setPackageForm("");
        setBasis("");
        setIngredientInfo("");
        setIngredientEng("");
        setPackImage("");
        setPillImage("");
        setIngredientCode("");
        setAtcCode("");
        setClassification("");
        setBasePrice("");
        setEfficacy("");
        setNote("");
        setIsDisplay("all");
        setKeyword("");
        setSearchKeyword("");
    }

    /* grid */

    const gridRef = useRef<Grid>(null);

    const processDataForGrid = (data: IAdminMedicine[]): Record<string, any>[] => {
        return data.map((item) => ({
            id: item.id,
            representativeCode: item.representativeCode,
            standardCode: item.standardCode,
            seqNo: item.seqNo,
            nameKor: item.nameKor,
            nameEng: item.nameEng,
            category: item.category,
            maker: item.maker,
            seller: item.seller,
            note: item.note,
            keywords: item.keywords,
            dosageRoute: item.dosageRoute,
            packageUnit: item.packageUnit,
            packageQuantity: item.packageQuantity,
            packageType: item.packageType,
            packageForm: item.packageForm,
            basis: item.basis,
            standard: item.standard,
            ingredientInfo: item.ingredientInfo,
            ingredientEng: item.ingredientEng,
            // packImageEdit 컬럼(버튼)에서 사용할 실제 medicine id
            packImageEdit: item.id,
            packImage: item.packImage,
            pillImage: item.pillImage,
            ingredientCode: item.ingredientCode,
            atcCode: item.atcCode,
            classification: item.classification,
            insuaranceCode: item.insuaranceCode,
            // basePrice: item.basePrice,
            // unitPrice: item.unitPrice,
            efficacy: item.efficacy,
            usage: item.usage,
            sideEffect: item.sideEffect,
            usagePrecaution: item.usagePrecaution,
            storagePrecaution: item.storagePrecaution,
            validityPeriod: item.validityPeriod,
            ingredientKor: item.ingredientKor,
            quantity: item.quantity,
            unit: item.unit,
            interaction: item.interaction,
            // insurancePrice: item.insurancePrice,
            coverage: item.coverage,
            priceStartDate: item.priceStartDate,
            cancelDate: item.cancelDate,
            permissionDate: item.permissionDate,
            // boolean 노출 컬럼
            isDisplay: (item as any).isDisplay,
        }));
    };

    const gridData = useMemo(() => processDataForGrid(medicineListData?.medicines ?? []), [medicineListData]);


    /////////////////////////////// pack image edit modal

    const [packImageEditMedicineId, setPackImageEditMedicineId] = useState<string>("");
    const {
        isOpen: isPackImageEditModalOpen,
        onOpen: onOpenPackImageEditModal,
        onClose: onClosePackImageEditModal,
    } = useDisclosure();

    const openPackImageEdit = (medicineId: string) => {
        setPackImageEditMedicineId(medicineId);
        onOpenPackImageEditModal();
    };

    const closePackImageEdit = () => {
        onClosePackImageEditModal();
        setPackImageEditMedicineId("");
    };

    // renderer 클래스가 최신 콜백을 바라볼 수 있게 주입
    PackImageEditButtonRenderer.onOpenHandler = openPackImageEdit;

    /////////////////////////////// columns

    const columnsNameArray = [
        "id",
        "representativeCode",
        "standardCode",
        "seqNo",
        "packImageEdit",
        "nameKor",
        // "basePrice",
        // "unitPrice",
        "nameEng",
        "category",
        "maker",
        "seller",
        "note",
        "keywords",
        "standard",
        "isDisplay",
        "dosageRoute",
        "packageUnit",
        "packageQuantity",
        "packageType",
        "packageForm",
        "basis",    
        "ingredientInfo",
        "ingredientEng",
        "packImage",
        "ingredientCode",
        "atcCode",
        "classification",
        "efficacy",
        "usage",
        "usagePrecaution",
        "sideEffect",
        "interaction",
    ];

    const columns = columnsNameArray.map((columnName) => {

        if(columnName === 'packImageEdit') {
            return {
                name: columnName,
                header: 'packImage edit',
                sortable: false,
                align: 'center',
                width: 120,
                renderer: {
                    type: PackImageEditButtonRenderer,
                    options: {
                        onOpen: openPackImageEdit,
                    },
                },
            } as any;
        } else if(columnName === 'id' || columnName === 'keywords') {
            return {
                name:columnName,
                header:columnName.charAt(0).toUpperCase() + columnName.slice(1),
                sortable:true,
                filter: { type:'text' as 'text', showClearBtn:true, showApplyBtn:true },
            }
        } else if (columnName === 'isDisplay') {
            return {
                name: columnName,
                header: columnName.charAt(0).toUpperCase() + columnName.slice(1),
                sortable: true,
                editor: 'text',
                formatter: ({ value }: FormatterProps) => (value === true ? 'true' : 'false'),
                filter: { type:'text' as 'text', showClearBtn:true, showApplyBtn:true },
            }
        } else {
            return {
                name:columnName,
                header:columnName.charAt(0).toUpperCase() + columnName.slice(1),
                sortable:true,
                editor: 'text',
                filter: { type:'text' as 'text', showClearBtn:true, showApplyBtn:true },
            }
        }
    });

    const isTrue = (value: string) => {
        return (value === 'true') 
        || (value === '\ntrue\n')
        || (value === 'TRUE') 
        || (value === '\nTRUE\n')
        || (value === 'y') 
        || (value === '\ny\n')
        || (value === 'Y') 
        || (value === '\nY\n');
    }

    /* checked rows */

    const [checkedRows, setCheckedRows] = useState<string[]>([]); // 체크된 row의 ID 저장
    const isChecked = (checkedRows.length > 0);

    const getCheckedRowsId = () => {
        const gridInstance = gridRef.current?.getInstance();
        if (!gridInstance) return [];
        // 체크된 행의 rowKey로 id 값을 가져와 배열에 저장
        return gridInstance.getCheckedRows().map((row: any) => row.id);
    };

    /* changed rows */

    const [changedRows, setChangedRows] = useState<any[]>([]); // 변경된 데이터를 저장할 상태
    const isChanged = (changedRows.length > 0);

    useEffect(() => {

        ////////////////////////////////////////////////////////////////////// 이벤트 핸들러 등록

        const gridInstance = gridRef.current?.getInstance();
        let currentScrollLeft = 0; // 스크롤 위치 저장 변수


        if(gridInstance) {

            ////////////////////////////////////////////////////////////////////// 그리드 컨테이너의 DOM 요소 가져오기

            const gridElement = document.querySelector('.tui-grid-rside-area .tui-grid-body-container')?.parentElement;

            ////////////////////////////////////////////////////////////////////// beforeChange

            gridInstance.on('beforeChange', () => {
                if (gridElement) {
                    currentScrollLeft = gridElement.scrollLeft;
                    console.log('Saved scrollLeft:', currentScrollLeft); // 스크롤 위치 확인
                }
            });

            ////////////////////////////////////////////////////////////////////// afterChange

            gridInstance.on('afterChange', (event:any) => {

                if (gridElement) { 
                    setTimeout(() => {
                        gridElement.scrollLeft = currentScrollLeft;
                        console.log('Restored scrollLeft:', gridElement.scrollLeft); // 스크롤 복원 확인
                    }, 0);
                }

                const changes = Array.isArray(event?.changes) ? event.changes : [];
                if (changes.length === 0) return;

                console.log('changes:', changes);

                ////////////////////////////////////////////////////////////////////// changes 배열을 순회하며 각 변경 사항 처리

                setChangedRows((prevChangedRows) => {

                    const updatedChanges = [...prevChangedRows]; // 기존 변경 사항 복사
        
                    changes.forEach((change: any) => {

                        const { rowKey, columnName } = change;
                        console.log('change:', change);

                        ////////////////////////////////////////////////////////////////////// nextValue 가져오기

                        const nextValue = (change && ('nextValue' in change)) ? change.nextValue : change?.value;

                        console.log('nextValue:', nextValue, typeof nextValue);

                        ////////////////////////////////////////////////////////////////////// newValue 가져오기

                        const newValue = (columnName === 'isDisplay')
                            ? ((nextValue === true) || isTrue(nextValue))
                            : nextValue;

                        console.log('newValue:', newValue, typeof newValue);

                        ////////////////////////////////////////////////////////////////////// rowData 가져오기

                        const rowData = gridInstance.getRow(rowKey);
                        const id = rowData?.id;

                        if(columnName === 'isDisplay') {
                            gridInstance.setValue(rowKey, 'isDisplay', newValue);
                        }
        
                        // 변경된 값을 찾아서 배열에 추가
                        if (id) {
                            const existingChangeIndex = updatedChanges.findIndex(
                                (item) => item.id === id && item.columnName === columnName
                            );
                            
                            // 이미 변경된 값이 있다면 업데이트
                            if (existingChangeIndex >= 0) {
                                updatedChanges[existingChangeIndex].value = newValue;
                            } else {
                            // 없다면 새롭게 추가
                                updatedChanges.push({ id, columnName, value: newValue });
                            }
                        }   
                    });

                    console.log("updatedChanges:", updatedChanges);

                    return updatedChanges;
                });
            })

            ////////////////////////////////////////////////////////////////////// check 이벤트

              gridInstance.on('check', (event: any) => {
                const newCheckedRows = getCheckedRowsId();
                console.log(newCheckedRows)
                setCheckedRows(newCheckedRows);
              });
        
            ////////////////////////////////////////////////////////////////////// uncheck 이벤트

              gridInstance.on('uncheck', (event: any) => {
                const newCheckedRows = getCheckedRowsId();
                console.log(newCheckedRows)
                setCheckedRows(newCheckedRows);
              });
        
            ////////////////////////////////////////////////////////////////////// checkAll 이벤트
            
              gridInstance.on('checkAll', () => {
                const newCheckedRows = getCheckedRowsId();
                console.log(newCheckedRows)
                setCheckedRows(newCheckedRows);
              });
        
            ////////////////////////////////////////////////////////////////////// uncheckAll 이벤트

              gridInstance.on('uncheckAll', () => {
                setCheckedRows([]); // 체크 해제 시 초기화
              });

            ////////////////////////////////////////////////////////////////////// cleanup function to remove the listener

            return () => {
                const gridInstance = gridRef.current?.getInstance();
                if (gridInstance) {
                    gridInstance.off('afterChange');
                    gridInstance.off('beforeChange');
                    gridInstance.off('check');
                    gridInstance.off('uncheck');
                    gridInstance.off('checkAll');
                    gridInstance.off('uncheckAll');
                }
            };
        }

        
    }, []);

    const onSave = async () => {
        let hasError = false;
        for (const row of changedRows) {
            const { id, columnName, value } = row;
            try {
                await updateMedicineMutation.mutateAsync({ medicineId: id, key: columnName, value: value });
            } catch {
                hasError = true;
            }
        }
        toast({
            title: hasError ? "약품 수정에 실패했습니다." : "약품 수정에 성공했습니다.",
            status: hasError ? "error" : "success",
            duration: 2000,
            isClosable: true,
        });
        setChangedRows([]);
    };

    /* modal */

    const { isOpen: isCreateMedModalOpen, onOpen: onOpenCreateMedModal, onClose: onCloseCreateMedModal } = useDisclosure();

    /* delete medicines modal */

    const { isOpen: isDeleteMedicinesModalOpen, onOpen: onOpenDeleteMedicinesModal, onClose: onCloseDeleteMedicinesModal } = useDisclosure();

    const onDeleteMedicines = () => {
        deleteMedicinesMutation.mutate({ medicineIds: checkedRows });
        onCloseDeleteMedicinesModal();
    }

    /* queryClient */

    const queryClient = useQueryClient();

    /* toast */

    const toast = useToast();

    /* delete medicines mutation */

    const deleteMedicinesMutation = useMutation({
        mutationFn: adminDeleteMedicines,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['adminGetMedicines', page, size, representativeCode, standardCode, seqNo, nameKor, nameEng, category, maker, seller, packageUnit, ingredientInfo, packImage, pillImage, ingredientCode, atcCode, classification, basePrice, efficacy, searchKeyword] });
            setCheckedRows([]);
            onCloseDeleteMedicinesModal();
            toast({
                title: "선택된 약품 삭제에 성공했습니다.",
                status: "success",
                position: "top",
                duration: 2000,
                isClosable: true,
            });
        },
        onError: (error) => {
            console.error(error);
            toast({
                title: "선택된 약품 삭제에 실패했습니다.",
                status: "error",
                position: "top",
                duration: 2000,
                isClosable: true,
            });
        },
    });


    /* inline edit: 셀 로컬 상태로 전환 (전역 상태로 인한 대량 리렌더 방지) */
    // 편집 불가 컬럼 (id, 생성/수정 일자)
    const uneditableKeys = new Set<string>(["id", "createdAt", "updatedAt"]);

    /* update field mutation */

    // 단일 필드 업데이트 요청
    // - 낙관적 업데이트: 해당 로우의 해당 필드만 캐시에서 즉시 반영 (재요청 없음)
    // - 실패 시 롤백
    // - 에러 토스트 노출

    const updateMedicineMutation = useMutation({
        mutationFn: adminUpdateMedicine,
        onMutate: async (variables) => {
            const { medicineId, key, value } = variables as { medicineId: string; key: string; value: any };
            await queryClient.cancelQueries({ queryKey: ['adminGetMedicines', page, size, representativeCode, standardCode, seqNo, nameKor, nameEng, category, maker, seller, packageUnit, ingredientInfo, packImage, pillImage, ingredientCode, atcCode, classification, basePrice, efficacy, searchKeyword] });
            const previous = queryClient.getQueryData<IAdminMedicineList>(['adminGetMedicines', page, size, representativeCode, standardCode, seqNo, nameKor, nameEng, category, maker, seller, packageUnit, ingredientInfo, packImage, pillImage, ingredientCode, atcCode, classification, basePrice, efficacy, searchKeyword]);
            if (previous) {
                const next = {
                    ...previous,
                    medicines: previous.medicines.map((medicine) => (medicine.id === medicineId ? { ...medicine, [key]: value } : medicine)),
                };
                queryClient.setQueryData(['adminGetMedicines', page, size, representativeCode, standardCode, seqNo, nameKor, nameEng, category, maker, seller, packageUnit, ingredientInfo, packImage, pillImage, ingredientCode, atcCode, classification, basePrice, efficacy, searchKeyword], next);
            }
            return { previous };
        },
        onError: (_error, _variables, context) => {
            const prev = (context as { previous?: IAdminMedicineList })?.previous;
            if (prev) {
                queryClient.setQueryData(['adminGetMedicines', page, size, representativeCode, standardCode, seqNo, nameKor, nameEng, category, maker, seller, packageUnit, ingredientInfo, packImage, pillImage, ingredientCode, atcCode, classification, basePrice, efficacy, searchKeyword], prev);
            }
        },
    });

    /* grid height */

    useEffect(() => {
        const gridInstance = gridRef.current?.getInstance();
        
        if (gridInstance) { 
          // 기본적으로 40px이 한 행의 높이라고 가정
          const rowHeight = 40; // 행의 높이
          const numRows = gridData.length; // 데이터의 행 수
    
          // 행 수에 맞춰 그리드 높이를 설정
          const gridHeight = rowHeight * numRows; // 헤더 높이를 고려하여 추가
          gridInstance.setBodyHeight(gridHeight);
        }
      }, [gridData]);










    /* blank modal */

    const { isOpen: isBlankModalOpen, onOpen: onOpenBlankModal, onClose: onCloseBlankModal } = useDisclosure();

    /* generate Fields */

    const generateFieldsMutation = useMutation({
        mutationFn: adminGenerateFields,
        onSuccess: () => {
            window.location.reload();
            toast({
                title: "필드 생성에 성공했습니다.",
                status: "success",
                position: "top",
                duration: 2000,
                isClosable: true,
            });
        },
        onError: (error) => {
            console.error(error);
            toast({
                title: "필드 생성에 실패했습니다.",
                status: "error",
                position: "top",
                duration: 2000,
                isClosable: true,
            });
        },
    });

    const onGenerateFields = async () => {
        await generateFieldsMutation.mutateAsync();
    }

    const onGridDblclick = (event: any) => {
        const rowKey = event?.rowKey;
        const columnName = event?.columnName;
        if (rowKey === undefined || rowKey === null || !columnName) return;
        if (columnName === 'packImageEdit' || columnName === 'id') return;

        const gridInstance = gridRef.current?.getInstance();
        if (!gridInstance) return;
        gridInstance.startEditing(rowKey, columnName);
    };

    const onExportCsv = () => {
        
        const gridInstance = gridRef.current?.getInstance();

        if (!gridInstance) {
            toast({
                title: "그리드가 준비되지 않았습니다.",
                status: "warning",
                position: "top",
                duration: 2000,
                isClosable: true,
            });
            return;
        }

        const csvColumnNames = columnsNameArray.filter((columnName) => columnName !== "packImageEdit");

        try {

            (gridInstance as any).export("csv", {
                fileName: `medicines-page-${page}`,
                columnNames: csvColumnNames,
            });

            toast({
                title: "CSV 다운로드 시작",
                status: "success",
                position: "top",
                duration: 2000,
                isClosable: true,
            });

        } catch (error) {
            console.error(error);
            toast({
                title: "CSV 다운로드 실패",
                status: "error",
                position: "top",
                duration: 2000,
                isClosable: true,
            });
        }
    };

    /* create companies */

    const createCompaniesMutation = useMutation({
        mutationFn: adminCreateCompanies,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['adminGetCompanies'] });
            toast({
                title: "제약사 생성에 성공했습니다.",
                status: "success",
                position: "top",
                duration: 2000,
                isClosable: true,
            });
        },
        onError: (error) => {
            console.error(error);
            toast({
                title: "제약사 생성에 실패했습니다.",
                status: "error",
                position: "top",
                duration: 2000,
                isClosable: true,
            });
        },
    });

    /* temp action */

    const tempActionMutation = useMutation({
        mutationFn: adminTempAction,
        onSuccess: ({ csv, fileName }) => {
            const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");

            link.href = url;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.setTimeout(() => URL.revokeObjectURL(url), 0);

            toast({
                title: "CSV 다운로드 시작",
                status: "success",
                position: "top",
                duration: 2000,
                isClosable: true,
            });
        },
        onError: (error) => {
            console.error(error);
            toast({
                title: "요청에 실패했습니다.",
                status: "error",
                position: "top",
                duration: 2000,
                isClosable: true,
            });
        },
    });

    /* render */

    return (
        <AdminOnly>
            <Stack width={"100%"} padding={10}>
                <HStack justifyContent={"space-between"} alignItems={"center"} gap={10}>

                    <HStack justifyContent={"flex-start"} alignItems={"center"} gap={3}>
                        <Text fontSize={28} fontWeight={700} color={colors.black}>약</Text>
                        <Text fontSize={28} fontWeight={400} color={colors.gray}>{medicineListData?.totalCount}</Text>
                    </HStack>
                    
                    <HStack gap={3}>

                        {/* create companies */}

                        <Box as="button" onClick={() => createCompaniesMutation.mutate()}>
                            {createCompaniesMutation.isPending ? <Spinner size="md" /> : <MyIcon src={"/svg/store.svg"} alt="packages" width={36} height={36} />}
                        </Box>

                        {/* temp action */}

                        <Box as="button" onClick={() => tempActionMutation.mutate()}>
                            {tempActionMutation.isPending ? <Spinner size="md" /> : <MyIcon src={"/svg/packages.svg"} alt="packages" width={36} height={36} />}
                        </Box>

                        {/* create medicine modal */}

                        <Box as="button" onClick={onOpenCreateMedModal}>
                            <MyIcon src={"/svg/plus-circle.svg"} alt="plus" width={36} height={36} />
                        </Box>
                        <CreateMedModal 
                            isOpen={isCreateMedModalOpen} 
                            onClose={onCloseCreateMedModal} 
                            page={page} 
                            size={size} 
                            representativeCode={representativeCode}
                            standardCode={standardCode}
                            seqNo={seqNo}
                            nameKor={nameKor}
                            nameEng={nameEng}
                            category={category}
                            maker={maker}
                            seller={seller}
                            packageUnit={packageUnit}
                            ingredientInfo={ingredientInfo}
                            packImage={packImage}
                            pillImage={pillImage}
                            ingredientCode={ingredientCode}
                            atcCode={atcCode}
                            classification={classification}
                            basePrice={basePrice}
                            efficacy={efficacy}
                            searchKeyword={searchKeyword}
                        />
                        <PackImageEditModal
                            isOpen={isPackImageEditModalOpen}
                            onClose={closePackImageEdit}
                            medicineId={packImageEditMedicineId}
                        />
                    </HStack>
                </HStack>
                <VStack 
                    as="form" 
                    onSubmit={handleSubmit(onSubmit)} 
                    marginY={10}
                    gap={3}

                    justifyContent={"flex-start"}
                    alignItems={"flex-start"}
                >
                    <RadioGroup value={isDisplay} onChange={setIsDisplay}>
                        <HStack spacing={6} marginY={4}>
                            <Box
                                paddingX={3}
                                paddingY={1}
                                backgroundColor={colors.light}
                                borderRadius={10}
                            >
                                <Text fontSize={12} fontWeight={500} color={colors.blue}>ISDISPLAY</Text>
                            </Box>
                            <Radio value="all" colorScheme="blue">모두</Radio>
                            <Radio value="true" colorScheme="blue">TRUE</Radio>
                            <Radio value="false" colorScheme="blue">FALSE</Radio>
                        </HStack>
                    </RadioGroup>
                    <SimpleGrid columns={4} spacing={3} width={"100%"}>
                        <FormControl>
                            <Input 
                                {...register("representativeCode", {required:false})}
                                type="text" 
                                placeholder="representativeCode"
                                isInvalid={Boolean(errors.representativeCode?.message)} 
                                width={"100%"}
                                borderColor={colors.gray}
                                borderRadius={10}
                                padding={3}
                                fontSize={16}
                                fontWeight={400}
                                color={colors.black}
                            />
                        </FormControl>
                        <FormControl>
                            <Input 
                                {...register("standardCode", {required:false})}
                                type="text" 
                                placeholder="standardCode"
                                isInvalid={Boolean(errors.standardCode?.message)} 
                                width={"100%"}
                                borderColor={colors.gray}
                                borderRadius={10}
                                padding={3}
                                fontSize={16}
                                fontWeight={400}
                                color={colors.black}
                            />
                        </FormControl>
                        <FormControl>
                            <Input 
                                {...register("seqNo", {required:false})}
                                type="text" 
                                placeholder="seqNo"
                                isInvalid={Boolean(errors.seqNo?.message)} 
                                width={"100%"}
                                borderColor={colors.gray}
                                borderRadius={10}
                                padding={3}
                                fontSize={16}
                                fontWeight={400}
                                color={colors.black}
                            />
                        </FormControl>
                        <FormControl>
                            <Input 
                                {...register("nameKor", {required:false})}
                                type="text" 
                                placeholder="nameKor"
                                isInvalid={Boolean(errors.nameKor?.message)} 
                                width={"100%"}
                                borderColor={colors.gray}
                                borderRadius={10}
                                padding={3}
                                fontSize={16}
                                fontWeight={400}
                                color={colors.black}
                            />
                        </FormControl>
                        <FormControl>
                            <Input 
                                {...register("nameEng", {required:false})}
                                type="text" 
                                placeholder="nameEng"
                                isInvalid={Boolean(errors.nameEng?.message)} 
                                width={"100%"}
                                borderColor={colors.gray}
                                borderRadius={10}
                                padding={3}
                                fontSize={16}
                                fontWeight={400}
                                color={colors.black}
                            />
                        </FormControl>
                        <FormControl>
                            <Input 
                                {...register("category", {required:false})}
                                type="text" 
                                placeholder="category"
                                isInvalid={Boolean(errors.category?.message)} 
                                width={"100%"}
                                borderColor={colors.gray}
                                borderRadius={10}
                                padding={3}
                                fontSize={16}
                                fontWeight={400}
                                color={colors.black}
                            />
                        </FormControl>
                        <FormControl>
                            <Input 
                                {...register("maker", {required:false})}
                                type="text" 
                                placeholder="maker"
                                isInvalid={Boolean(errors.maker?.message)} 
                                width={"100%"}
                                borderColor={colors.gray}
                                borderRadius={10}
                                padding={3}
                                fontSize={16}
                                fontWeight={400}
                                color={colors.black}
                            />
                        </FormControl>
                        <FormControl>
                            <Input 
                                {...register("seller", {required:false})}
                                type="text" 
                                placeholder="seller"
                                isInvalid={Boolean(errors.seller?.message)} 
                                width={"100%"}
                                borderColor={colors.gray}
                                borderRadius={10}
                                padding={3}
                                fontSize={16}
                                fontWeight={400}
                                color={colors.black}
                            />
                        </FormControl>
                        <FormControl>
                            <Input 
                                {...register("dosageRoute", {required:false})}
                                type="text" 
                                placeholder="dosageRoute"
                                isInvalid={Boolean(errors.dosageRoute?.message)} 
                                width={"100%"}
                                borderColor={colors.gray}
                                borderRadius={10}
                                padding={3}
                                fontSize={16}
                                fontWeight={400}
                                color={colors.black}
                            />
                        </FormControl>
                        <FormControl>
                            <Input 
                                {...register("packageUnit", {required:false})}
                                type="text" 
                                placeholder="packageUnit"
                                isInvalid={Boolean(errors.packageUnit?.message)} 
                                width={"100%"}
                                borderColor={colors.gray}
                                borderRadius={10}
                                padding={3}
                                fontSize={16}
                                fontWeight={400}
                                color={colors.black}
                            />
                        </FormControl>
                        <FormControl>
                            <Input 
                                {...register("packageQuantity", {required:false})}
                                type="text" 
                                placeholder="packageQuantity"
                                isInvalid={Boolean(errors.packageQuantity?.message)} 
                                width={"100%"}
                                borderColor={colors.gray}
                                borderRadius={10}
                                padding={3}
                                fontSize={16}
                                fontWeight={400}
                                color={colors.black}
                            />
                        </FormControl>
                        <FormControl>
                            <Input 
                                {...register("packageType", {required:false})}
                                type="text" 
                                placeholder="packageType"
                                isInvalid={Boolean(errors.packageType?.message)} 
                                width={"100%"}
                                borderColor={colors.gray}
                                borderRadius={10}
                                padding={3}
                                fontSize={16}
                                fontWeight={400}
                                color={colors.black}
                            />
                        </FormControl>
                        <FormControl>
                            <Input 
                                {...register("packageForm", {required:false})}
                                type="text" 
                                placeholder="packageForm"
                                isInvalid={Boolean(errors.packageForm?.message)} 
                                width={"100%"}
                                borderColor={colors.gray}
                                borderRadius={10}
                                padding={3}
                                fontSize={16}
                                fontWeight={400}
                                color={colors.black}
                            />
                        </FormControl>
                        <FormControl>
                            <Input 
                                {...register("basis", {required:false})}
                                type="text" 
                                placeholder="basis"
                                isInvalid={Boolean(errors.basis?.message)} 
                                width={"100%"}
                                borderColor={colors.gray}
                                borderRadius={10}
                                padding={3}
                                fontSize={16}
                                fontWeight={400}
                                color={colors.black}
                            />
                        </FormControl>
                        <FormControl>
                            <Input 
                                {...register("ingredientInfo", {required:false})}
                                type="text" 
                                placeholder="ingredientInfo"
                                isInvalid={Boolean(errors.ingredientInfo?.message)} 
                                width={"100%"}
                                borderColor={colors.gray}
                                borderRadius={10}
                                padding={3}
                                fontSize={16}
                                fontWeight={400}
                                color={colors.black}
                            />
                        </FormControl>
                        <FormControl>
                            <Input 
                                {...register("ingredientEng", {required:false})}
                                type="text" 
                                placeholder="ingredientEng"
                                isInvalid={Boolean(errors.ingredientEng?.message)} 
                                width={"100%"}
                                borderColor={colors.gray}
                                borderRadius={10}
                                padding={3}
                                fontSize={16}
                                fontWeight={400}
                                color={colors.black}
                            />
                        </FormControl>
                        <FormControl>
                            <Input 
                                {...register("packImage", {required:false})}
                                type="text" 
                                placeholder="packImage"
                                isInvalid={Boolean(errors.packImage?.message)} 
                                width={"100%"}
                                borderColor={colors.gray}
                                borderRadius={10}
                                padding={3}
                                fontSize={16}
                                fontWeight={400}
                                color={colors.black}
                            />
                        </FormControl>
                        <FormControl>
                            <Input 
                                {...register("pillImage", {required:false})}
                                type="text" 
                                placeholder="pillImage"
                                isInvalid={Boolean(errors.pillImage?.message)} 
                                width={"100%"}
                                borderColor={colors.gray}
                                borderRadius={10}
                                padding={3}
                                fontSize={16}
                                fontWeight={400}
                                color={colors.black}
                            />
                        </FormControl>
                        <FormControl>
                            <Input 
                                {...register("ingredientCode", {required:false})}
                                type="text" 
                                placeholder="ingredientCode"
                                isInvalid={Boolean(errors.ingredientCode?.message)} 
                                width={"100%"}
                                borderColor={colors.gray}
                                borderRadius={10}
                                padding={3}
                                fontSize={16}
                                fontWeight={400}
                                color={colors.black}
                            />
                        </FormControl>
                        <FormControl>
                            <Input 
                                {...register("atcCode", {required:false})}
                                type="text" 
                                placeholder="atcCode"
                                isInvalid={Boolean(errors.atcCode?.message)} 
                                width={"100%"}
                                borderColor={colors.gray}
                                borderRadius={10}
                                padding={3}
                                fontSize={16}
                                fontWeight={400}
                                color={colors.black}
                            />
                        </FormControl>
                        <FormControl>
                            <Input 
                                {...register("classification", {required:false})}
                                type="text" 
                                placeholder="classification"
                                isInvalid={Boolean(errors.classification?.message)} 
                                width={"100%"}
                                borderColor={colors.gray}
                                borderRadius={10}
                                padding={3}
                                fontSize={16}
                                fontWeight={400}
                                color={colors.black}
                            />
                        </FormControl>
                        {/* <FormControl>
                            <Input 
                                {...register("basePrice", {required:false})}
                                type="text" 
                                placeholder="basePrice"
                                isInvalid={Boolean(errors.basePrice?.message)} 
                                width={"100%"}
                                borderColor={colors.gray}
                                borderRadius={10}
                                padding={3}
                                fontSize={16}
                                fontWeight={400}
                                color={colors.black}
                            />
                        </FormControl> */}
                        <FormControl>
                            <Input 
                                {...register("efficacy", {required:false})}
                                type="text" 
                                placeholder="efficacy"
                                isInvalid={Boolean(errors.efficacy?.message)} 
                                width={"100%"}
                                borderColor={colors.gray}
                                borderRadius={10}
                                padding={3}
                                fontSize={16}
                                fontWeight={400}
                                color={colors.black}
                            />
                        </FormControl>
                        <FormControl>
                            <Input 
                                {...register("searchKeyword", {required:false})}
                                type="text" 
                                placeholder="searchKeyword"
                                isInvalid={Boolean(errors.searchKeyword?.message)} 
                                width={"100%"}
                                borderColor={colors.gray}
                                borderRadius={10}
                                padding={3}
                                fontSize={16}
                                fontWeight={400}
                                color={colors.black}
                            />
                        </FormControl>
                        <FormControl>
                            <Input 
                                {...register("note", {required:false})}
                                type="text" 
                                placeholder="note"
                                isInvalid={Boolean(errors.note?.message)} 
                                width={"100%"}
                                borderColor={colors.gray}
                                borderRadius={10}
                                padding={3}
                                fontSize={16}
                                fontWeight={400}
                                color={colors.black}
                            />
                        </FormControl>
                        <FormControl>
                            <Input 
                                {...register("keyword", {required:false})}
                                type="text" 
                                placeholder="keyword"
                                isInvalid={Boolean(errors.keyword?.message)} 
                                width={"100%"}
                                borderColor={colors.gray}
                                borderRadius={10}
                                padding={3}
                                fontSize={16}
                                fontWeight={400}
                                color={colors.black}
                            />
                        </FormControl>
                        <HStack spacing={3} justifyContent={"center"} alignItems={"center"}> 
                            <Button 
                                type="submit" 
                                backgroundColor={colors.white} 
                                borderColor={colors.blue}
                                borderWidth={1}
                                borderRadius={10} 
                                _hover={{ bg: colors.light }} 
                                boxShadow="sm"
                                width={"50%"}
                                isLoading={isMedicineListDataLoading}
                            > 
                                <MyIcon src={"/svg/search.svg"} alt="search" width={24} height={24} />
                            </Button>
                            <Button 
                                backgroundColor={colors.white} 
                                borderColor={colors.brown}
                                borderWidth={1}
                                borderRadius={10} 
                                _hover={{ bg: colors.bubble }} 
                                boxShadow="sm"
                                width={"50%"}

                                onClick={onReset}
                            > 
                                🧹
                            </Button>
                        </HStack>
                    </SimpleGrid>
                </VStack>

                {/* trash button */}

                <HStack justifyContent={"flex-start"} marginY={0}>
                    <Button 
                        size={"sm"} 
                        variant={"solid"} 
                        backgroundColor={!isChecked ? colors.g5 : colors.g3}
                        isDisabled={!isChecked} 
                        opacity={!isChecked ? 0.1 : 1}
                        onClick={onOpenDeleteMedicinesModal}
                    >
                        <MyIcon src={"/svg/trash-filled.svg"} alt="trash" width={16} height={16} />
                    </Button>
                    <Button
                        size={"sm"} 
                        variant={"solid"} 
                        backgroundColor={!isChanged ? colors.g5 : colors.link}
                        isDisabled={!isChanged} 
                        opacity={!isChanged ? 0.1 : 1}
                        onClick={onSave}
                    >
                        {!isChanged ? <MyIcon src={"/svg/check.svg"} alt="save" width={16} height={16} /> : <MyIcon src={"/svg/check-green.svg"} alt="save" width={16} height={16} />}
                    </Button>
                    <Button
                        size={"sm"} 
                        variant={"solid"} 
                        backgroundColor={gridData.length > 0 ? colors.spotify : colors.g5}
                        isDisabled={gridData.length === 0}
                        opacity={gridData.length === 0 ? 0.4 : 1}
                        color={colors.black}
                        _hover={{ bg: colors.spotify }} 
                        boxShadow="sm"
                        onClick={onExportCsv}
                    >
                        ⇣
                    </Button>

                    {/* delete medicines modal */}

                    <Modal isOpen={isDeleteMedicinesModalOpen} onClose={onCloseDeleteMedicinesModal}>
                        <ModalOverlay />
                        <ModalContent>
                            <ModalHeader>
                                <Text fontSize={20} fontWeight={700} color={colors.black}>삭제하기</Text>
                                <ModalCloseButton />
                            </ModalHeader>
                            <ModalBody>
                                <Text fontSize={16} fontWeight={400} color={colors.black}>선택된 행 수: {checkedRows.length}</Text>
                                <Text fontSize={16} fontWeight={400} color={colors.black}>정말 삭제하시겠습니까?</Text>
                            </ModalBody>
                            <Stack marginTop={5} width={"100%"} padding={3}>
                                <Button
                                    onClick={onDeleteMedicines}
                                    isLoading={deleteMedicinesMutation.isPending}
                                    width={"100%"}
                                    backgroundColor={colors.blue}
                                    color={colors.white}
                                    borderRadius={10}
                                    _hover={{ bg: colors.blue }}
                                    boxShadow="sm"
                                >
                                    삭제
                                </Button>
                            </Stack>
                        </ModalContent>
                    </Modal>
                </HStack>

                {/* Grid */}

                <Grid
                    ref={gridRef}
                    data={isMedicineListDataLoading ? [] : gridData}
                    columns={columns}
                    onDblclick={onGridDblclick} // double click event
                    columnOptions={{
                        minWidth: 160,
                        resizable: true,
                    }}
                    rowHeaders={['checkbox']}
                    bodyHeight={'fitToParent'}
                    heightResizable={true}
                />

                {/* pagination */}

                <HStack justifyContent={"center"} marginTop={5} >
                    {/* 이전 페이지 버튼 */}
                    {page > 1 && (
                        <Button size={"xs"} variant={"ghost"} onClick={() => setPage(page - 1)}>Previous</Button>
                    )}
            
                    {/* 첫 페이지와 '...' */}
                    {page > 5 && (
                        <>
                            <Button size={"xs"} variant={"ghost"} onClick={() => setPage(1)}>1</Button>
                            <Text>...</Text>
                        </>
                    )}
            
                    {/* 페이지 버튼들 */}
                    {pages.map((page) => (
                        <Button
                            key={page}
                            size={"xs"} variant={"ghost"}
                            onClick={() => setPage(page)}
                            backgroundColor={page === currentPage ? colors.blue : ""}
                            color={page === currentPage ? colors.white : ""}
                        >
                            {page}
                        </Button>
                    ))}
            
                    {/* 마지막 페이지와 '...' */}
                    {page < totalPages - 4 && (
                        <>
                            <Text>...</Text>
                            <Button size={"xs"} variant={"ghost"} onClick={() => setPage(totalPages)}>{totalPages}</Button>
                        </>
                    )}
            
                    {/* 다음 페이지 버튼 */}
                    {page < totalPages && (
                        <Button size={"xs"} variant={"ghost"} onClick={() => setPage(page + 1)}>Next</Button>
                    )}
                </HStack>
            </Stack>
        </AdminOnly>
    )
}